import { ActionIconButton } from '@dealstack/frontend/shared/components/ActionIconButton/ActionIconButton.tsx';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { createUnitsColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/UnitsColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { HYPHEN_MINUS, LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number';
import {
	INVESTOR_TEST_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';
import { Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import type { HoldingsTableRow } from './utils/types';

interface Props {
	tInvestor: TranslateFunction<I18Namespaces.INVESTOR>;
	isValueColumnVisible: boolean;
	showAllHoldingsMode: boolean;
	securityValuationDate?: string | null;
}

export const getInvestorHoldingsTableColumns = ({
	tInvestor,
	isValueColumnVisible,
	showAllHoldingsMode,
	securityValuationDate,
}: Props): ExtendedColumnDef<HoldingsTableRow>[] => {
	const cols: ExtendedColumnDef<HoldingsTableRow>[] = [
		createTextColumn({
			id: 'issuingCompany',
			headerText: tInvestor('valuations.holding_table.headers.issuing_company'),
			accessorKey: 'entityName',
			enableSorting: false,
		}),
		{
			id: 'program',
			header: () => (
				<Text fw={600}>{tInvestor('valuations.holding_table.headers.program')}</Text>
			),
			cell: ({ row }) => {
				if (row.original._loans) {
					return null;
				}
				return (
					<Flex direction="column" gap="0.25rem">
						<Text>{row.original.programmeName}</Text>
						<Text c="dark.0">{row.original.targetName}</Text>
					</Flex>
				);
			},
		},
		createTextColumn({
			id: 'security',
			headerText: tInvestor('valuations.holding_table.headers.security'),
			accessorKey: 'name',
			enableSorting: false,
		}),
		createUnitsColumn({
			id: 'volume',
			headerText: tInvestor('valuations.holding_table.headers.volume'),
			accessorKey: 'volume',
			enableSorting: false,
		}),
		createNumberColumn({
			id: 'cost',
			headerText: tInvestor('valuations.holding_table.headers.cost'),
			lockPrecision: true,
			accessorFn: (row) => ({ value: row.cost, currency: row.securityCurrency }),
			enableSorting: false,
		}),
	];

	if (isValueColumnVisible) {
		cols.push({
			id: 'value',
			header: () => (
				<Flex direction="column" justify="flex-end" w="100%">
					<Text ta="right" fw={600}>
						{tInvestor('valuations.holding_table.headers.value.v2')}
					</Text>
					{!showAllHoldingsMode && securityValuationDate && (
						<Text ta="right">
							{`(${formatStringDateToUI(securityValuationDate, DATE_FORMATS.CALENDAR_DATE) ?? HYPHEN_MINUS})`}
						</Text>
					)}
				</Flex>
			),
			cell: ({ row }) => {
				if (typeof row.original.value !== 'number') {
					return (
						<Flex justify="flex-end" w="100%">
							{LONG_DASH}
						</Flex>
					);
				}
				return (
					<Flex direction="column" align="flex-end" w="100%">
						<Text>
							{formatNumberToUI(row.original.value, row.original.securityCurrency)}
						</Text>
						{showAllHoldingsMode && securityValuationDate && (
							<Text>
								{`(${formatStringDateToUI(securityValuationDate, DATE_FORMATS.CALENDAR_DATE)})`}
							</Text>
						)}
					</Flex>
				);
			},
		});
	}

	cols.push({
		id: 'vesting',
		header: () => (
			<Flex justify="center" w="100%">
				<Text fw={600}>{tInvestor('valuations.holding_table.headers.vesting')}</Text>
			</Flex>
		),
		size: 80,
		cell: ({ row }) => (
			<Flex justify="center" w="100%">
				{row.original.isSubjectToVesting ? (
					<ActionIconButton
						iconVariant={row.getIsExpanded() ? 'unview-v3' : 'eye'}
						onClick={(e) => {
							e.stopPropagation();
							row.toggleExpanded();
						}}
						dataTestId={makeComplexDataTestId(
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_HOLDINGS_TABLE_VESTING_ICON,
							row.original.securityId
						)}
					/>
				) : (
					<Text>n/a</Text>
				)}
			</Flex>
		),
	});

	return cols;
};
